import { useState } from 'react';
import { useSession } from 'src/hooks';
import { Modal } from 'src/components/Modal';
import { Button } from 'src/v2/commonComponents/Button';
import { InputSimple } from 'src/v2/commonComponents/InputSimple';
import { useCreateApiKeyMutation } from 'src/store/services/keymasterApi';
import { CopyAPIKeyField } from '../CopyAPIKeyField';
import styles from './CreateAPIKeyModal.module.scss';
import { toast } from 'react-toastify';
import { COMMON_ERROR_TEXT } from 'src/constants';
import { sendGTMEvent } from 'src/utils';
import { GTMEvent } from 'src/types';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateApiKeyModal = ({
  isOpen,
  onClose,
}: CreateApiKeyModalProps) => {
  const {
    appUser: { user_id },
  } = useSession();

  const [keyName, setKeyName] = useState<string>('');
  const [createdApiKey, setCreatedApiKey] = useState<string>('');
  const [createApiKey, { isLoading }] = useCreateApiKeyMutation();

  const handleSubmit = async () => {
    try {
      const result = await createApiKey({ user_id, keyName }).unwrap();

      if (result?.api_key?.plain_api_key) {
        setCreatedApiKey(result?.api_key?.plain_api_key);
        sendGTMEvent(GTMEvent.CREATE_API_KEY);
      }
    } catch (error) {
      toast.error(COMMON_ERROR_TEXT);
    }
  };

  const handleResetState = () => {
    setKeyName('');
    setCreatedApiKey('');
  };

  const isDisabled = !keyName.trim() || isLoading;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={styles.root}
      overlayClassName={styles.overlay}
      onAnimationEnd={handleResetState}
    >
      <h5 className={styles.title}>
        {!!createdApiKey ? 'Your API Key' : 'Create new key'}
      </h5>

      <div className={styles.body}>
        <p className={styles.text}>
          {!!createdApiKey
            ? 'Important note: You can view your key only once. Keep it secure, as anyone with access can use it. If needed, you can generate a new one at any time.'
            : 'This API key is tied to your workspace and can only be used by you.'}
        </p>

        {!createdApiKey ? (
          <InputSimple
            placeholder="Enter key name (e.g. Production Key)"
            value={keyName}
            label="Name"
            isRequired
            onChange={setKeyName}
          />
        ) : (
          <CopyAPIKeyField text={createdApiKey} />
        )}
      </div>

      {!createdApiKey ? (
        <div className={styles.buttonsWrapper}>
          <Button color="secondary-outline" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isDisabled}>
            Create key
          </Button>
        </div>
      ) : (
        <div className={styles.buttonsWrapper}>
          <Button color="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
};
